import {
  PointerEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ButtonStyled } from '../../../components/button/button.styled';
import type { ActiveAnnouncement } from '../../../types/api/announcements.types';
import { AnnouncementContent, DONT_SHOW_AGAIN_LABEL } from './AnnouncementView';
import { DontShowAgain } from './AnnouncementView.styled';
import { Controls, Dot, Dots, Slide } from './AnnouncementCarousel.styled';

/** How far a drag has to travel before it turns the page. */
const SWIPE_DISTANCE = 60;
/** Movement below this is a click or a wobble, not a drag. */
const DRAG_SLOP = 8;

type SlidesProps = {
  items: ActiveAnnouncement[];
  index: number;
  onIndex: (index: number) => void;
};

/**
 * The announcement on screen, paged by swiping (touch, or a mouse drag) and
 * by the arrow keys.
 *
 * Every page change scrolls the modal back to the top. The modal stays open
 * between slides, so without it the next announcement opened wherever the
 * last one had been scrolled to - usually the bottom, where its button was -
 * with its heading and picture cut off above.
 */
export const AnnouncementSlides = ({ items, index, onIndex }: SlidesProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const previous = useRef(index);
  const [from, setFrom] = useState<'left' | 'right' | null>(null);

  const drag = useRef<{ x: number; y: number; active: boolean } | null>(null);
  const justDragged = useRef(false);
  const [offset, setOffset] = useState(0);

  const item = items[index];

  useLayoutEffect(() => {
    if (previous.current !== index)
      setFrom(index > previous.current ? 'right' : 'left');
    previous.current = index;

    // The nearest scrolling ancestor is the modal's own scroll box. Stop
    // there: anything further up is the page, which must not move.
    let el = rootRef.current?.parentElement ?? null;
    while (el && !/(auto|scroll)/.test(getComputedStyle(el).overflowY))
      el = el.parentElement;
    if (el) el.scrollTop = 0;
  }, [index, item?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') onIndex(index + 1);
      if (e.key === 'ArrowLeft') onIndex(index - 1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, onIndex]);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (items.length < 2 || (e.pointerType === 'mouse' && e.button !== 0))
      return;
    drag.current = { x: e.clientX, y: e.clientY, active: false };
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;

    if (!d.active) {
      // Mostly vertical is a scroll, and the browser keeps it.
      if (Math.abs(dx) < DRAG_SLOP || Math.abs(dx) < Math.abs(e.clientY - d.y))
        return;
      d.active = true;
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    // Past either end it still moves, at a third of the pace, so the edge
    // reads as an edge instead of as a broken swipe.
    const atEdge =
      (dx > 0 && index === 0) || (dx < 0 && index === items.length - 1);
    setOffset(atEdge ? dx / 3 : dx);
  };

  const endDrag = () => {
    const d = drag.current;
    drag.current = null;
    if (!d?.active) return;

    justDragged.current = true;
    if (offset <= -SWIPE_DISTANCE) onIndex(index + 1);
    if (offset >= SWIPE_DISTANCE) onIndex(index - 1);
    setOffset(0);
  };

  if (!item) return null;

  return (
    <div ref={rootRef}>
      <Slide
        key={item.id}
        $from={from}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        // A drag that ends over a link in the body must not also follow it.
        onClickCapture={(e) => {
          if (!justDragged.current) return;
          justDragged.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* The drag moves an inner box: the slide's own entrance animation
            holds `transform` on the outer one, and an animation beats an
            inline style. */}
        <div
          style={{
            transform: offset ? `translateX(${offset}px)` : undefined,
            transition: offset ? 'none' : 'transform 200ms ease',
          }}
        >
          <AnnouncementContent
            type={item.type}
            title={item.title}
            body={item.body}
            imageUrl={item.imageUrl}
            gifUrl={item.gifUrl}
          />
        </div>
      </Slide>
    </div>
  );
};

type ControlsProps = {
  count: number;
  index: number;
  onIndex: (index: number) => void;
  /** The last slide's button. */
  onDone: () => void;
  /** The slide on screen repeats, so it can be silenced for good. */
  showDontShowAgain: boolean;
  onDontShowAgain: () => void;
};

/**
 * Dots and the one button, pinned to the bottom of the modal so they never
 * scroll away under a long announcement. The button says Next until the last
 * slide, where it becomes Got It and closes.
 */
export const AnnouncementCarouselControls = ({
  count,
  index,
  onIndex,
  onDone,
  showDontShowAgain,
  onDontShowAgain,
}: ControlsProps) => {
  const isLast = index >= count - 1;

  return (
    <Controls>
      {count > 1 && (
        <Dots role="tablist" aria-label="Updates">
          {Array.from({ length: count }, (_, i) => (
            <Dot
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Update ${i + 1} of ${count}`}
              $active={i === index}
              onClick={() => onIndex(i)}
            />
          ))}
        </Dots>
      )}

      {/* ButtonStyled rather than Button: that one reaches for the barrel. */}
      <ButtonStyled
        type="button"
        $selected
        $w="100%"
        onClick={() => (isLast ? onDone() : onIndex(index + 1))}
      >
        <span>{isLast ? 'Got It' : 'Next'}</span>
      </ButtonStyled>

      {showDontShowAgain && (
        <DontShowAgain style={{ margin: 0 }} onClick={onDontShowAgain}>
          {DONT_SHOW_AGAIN_LABEL}
        </DontShowAgain>
      )}
    </Controls>
  );
};
